import { spawn } from "node:child_process";
import { LocalCliError } from "./errors.js";

/** Run an `az` CLI command, resolving stdout. Throws LocalCliError on failure. */
export function runAz(args: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const proc = spawn("az", args, { stdio: ["ignore", "pipe", "pipe"] });
    const out: Buffer[] = [];
    const err: Buffer[] = [];
    proc.stdout.on("data", (d: Buffer) => out.push(d));
    proc.stderr.on("data", (d: Buffer) => err.push(d));
    proc.on("error", (e) => {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") {
        reject(
          new LocalCliError({
            code: "AZ_NOT_INSTALLED",
            message: "The 'az' command is not installed or not on PATH.",
            hint: "Install Azure CLI: https://learn.microsoft.com/cli/azure/install-azure-cli",
          }),
        );
        return;
      }
      reject(e);
    });
    proc.on("close", (code) => {
      const errString = Buffer.concat(err).toString("utf8");
      if (code !== 0) {
        if (/please run.*az login/i.test(errString) || /not logged in/i.test(errString)) {
          reject(
            new LocalCliError({
              code: "AUTH_NOT_SIGNED_IN",
              message: "You are not signed in to Azure.",
              hint: "Run 'az login' to sign in.",
            }),
          );
          return;
        }
        reject(
          new LocalCliError({
            code: "AZ_COMMAND_FAILED",
            message: `'az ${args.join(" ")}' failed: ${errString.trim()}`,
          }),
        );
        return;
      }
      resolve(Buffer.concat(out).toString("utf8"));
    });
  });
}

/**
 * Rethrow only az-environment failures (not signed in / not installed) — those
 * must surface clearly. Every other failure (notably a not-found resource, which
 * `runAz` reports as AZ_COMMAND_FAILED) is left for the caller to treat as
 * "absent", with an idempotent create as the backstop.
 */
export function rethrowIfAzUnavailable(e: unknown): void {
  if (e instanceof LocalCliError && (e.code === "AUTH_NOT_SIGNED_IN" || e.code === "AZ_NOT_INSTALLED")) {
    throw e;
  }
}
